/**
 * Admin — Bulk Kit Order Packing Screen
 * Route: /admin/bulk-kit-orders/:orderId/packing
 *
 * Feb 2026 (SCWT-BULK-BOX-DIMS) — dedicated packing workflow.
 * Renders one card per physical box with:
 *   • Outbound label download
 *   • 6 return labels (kits 1-6 for Box 1, 7-12 for Box 2, etc.)
 *   • "Print Box N" button — opens all N labels in a single tab per box
 *   • "Print entire order" — opens every label in the order
 *   • Dispatch controls (mark shipped, tracking override)
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, Truck, Package, Send, MailCheck, ExternalLink } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import api from "@/lib/api";
import { toast } from "sonner";

const printUrls = (urls) => {
  urls.filter(Boolean).forEach((u, i) => setTimeout(() => window.open(u, "_blank", "noopener"), i * 250));
};

export default function AdminBulkKitPacking() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/bulk-kit-orders/${orderId}/packing`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load order"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [orderId]);

  const markShipped = async () => {
    if (!window.confirm("Mark this order as shipped? This will bump partner inventory and email the partner.")) return;
    setDispatching(true);
    try {
      await api.post(`/admin/bulk-kit-orders/${orderId}/mark-shipped`, {});
      toast.success("Order marked as shipped — partner has been notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark shipped");
    } finally { setDispatching(false); }
  };

  const previewEmail = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/bulk-kit-orders/${orderId}/preview-dispatch-email`, {});
      toast.success(`Preview sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send preview");
    } finally { setPreviewing(false); }
  };

  const printEntireOrder = () => {
    if (!data) return;
    printUrls([...(data.outbound_label_urls || []), ...(data.return_label_urls || [])]);
  };

  const printBox = (group) => {
    const urls = [
      group.outbound?.label_url,
      ...(group.returns || []).map((r) => r.label_url),
    ];
    printUrls(urls);
  };

  const isShipped = data?.status === "shipped";

  return (
    <PortalShell
      title={`Packing — ${data?.order_number || orderId}`}
      subtitle={data ? `${data.partner?.business_name || "Partner"} · ${data.boxes} box${data.boxes === 1 ? "" : "es"} · ${data.kits} kits` : ""}
    >
      <div className="max-w-5xl mx-auto space-y-6" data-testid="bulk-kit-packing-page">
        <Link
          to="/admin/bulk-kit-orders"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" /> Back to bulk kit orders
        </Link>

        {loading && <div className="text-slate-500 text-sm">Loading…</div>}

        {!loading && data && (
          <>
            {/* Summary bar
                SCWT-BULK-PACKING-PARTNER-NAME-WRAP · Feb 2026 —
                the previous 5-column equal-width grid squeezed the
                partner name into 20% and clipped it with an
                ellipsis (e.g. "Pristine Water System..." for
                "Pristine Water Systems Top End"). Now uses a 6-col
                grid with Partner spanning 2 cols, and the Partner
                Stat renders with `wrap` so long names can flow onto
                two lines instead of clipping. */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 grid grid-cols-2 sm:grid-cols-6 gap-4">
              <div className="sm:col-span-2">
                <Stat label="Partner" value={data.partner?.business_name || "—"} big wrap />
              </div>
              <Stat label="Order" value={data.order_number || "—"} mono />
              <Stat label="Boxes" value={data.boxes} />
              <Stat label="Kits" value={data.kits} />
              <Stat label="Status" value={data.status || "—"} pill />
            </div>

            {/* Action row */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={printEntireOrder}
                data-testid="print-entire-order-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              >
                <Printer className="w-4 h-4" /> Print entire order
              </button>
              <button
                type="button"
                onClick={previewEmail}
                disabled={previewing}
                data-testid="preview-dispatch-email-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <MailCheck className="w-4 h-4" /> {previewing ? "Sending…" : "Preview dispatch email"}
              </button>
              <div className="flex-1" />
              {!isShipped && (
                <button
                  type="button"
                  onClick={markShipped}
                  disabled={dispatching}
                  data-testid="mark-shipped-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Truck className="w-4 h-4" /> {dispatching ? "Marking…" : "Mark as shipped"}
                </button>
              )}
              {isShipped && (
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-800 text-sm font-semibold">
                  <Truck className="w-4 h-4" /> Shipped {data.shipped_at ? "· " + new Date(data.shipped_at).toLocaleString() : ""}
                </span>
              )}
            </div>

            {/* Per-box cards */}
            <div className="space-y-4">
              {(data.grouped_by_box || []).map((g) => (
                <BoxCard key={g.box_index} group={g} onPrint={() => printBox(g)} />
              ))}
            </div>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function BoxCard({ group, onPrint }) {
  const box = group.box_index;
  const outbound = group.outbound;
  const returns = group.returns || [];
  return (
    <div
      data-testid={`packing-box-card-${box}`}
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      <div className="px-5 py-4 bg-slate-50 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center">
            <Package className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">Box</div>
            <div className="text-lg font-bold text-slate-900">
              #{box} <span className="text-xs font-normal text-slate-500">· {group.kits_per_box} kits</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onPrint}
          data-testid={`print-box-${box}-btn`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
        >
          <Printer className="w-3.5 h-3.5" /> Print Box {box}
        </button>
      </div>

      {/* Outbound row */}
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500">Outbound label</div>
            <div className="font-mono text-sm text-slate-900">
              {outbound?.tracking || <span className="text-slate-400">not yet generated</span>}
            </div>
          </div>
          {outbound?.label_url && (
            <a
              href={outbound.label_url} target="_blank" rel="noopener noreferrer"
              data-testid={`box-${box}-outbound-label-link`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
            >
              Open label <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      {/* Return labels grid */}
      <div className="px-5 py-4">
        <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500 mb-2">
          Return labels ({returns.length})
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {returns.map((r) => (
            <a
              key={r.kit_index}
              href={r.label_url || "#"}
              target={r.label_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid={`box-${box}-return-${r.kit_in_box}-link`}
              onClick={(e) => { if (!r.label_url) e.preventDefault(); }}
              className={`rounded-lg border p-2 text-xs ${r.label_url ? "border-slate-200 hover:border-cyan-400 hover:bg-cyan-50" : "border-slate-100 bg-slate-50 cursor-default"}`}
            >
              <div className="text-[10px] text-slate-500">Kit {r.kit_in_box} of {group.kits_per_box}</div>
              <div className="font-mono text-slate-900 truncate">{r.tracking || "—"}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, big, mono, pill, wrap }) {
  // SCWT-BULK-PACKING-PARTNER-NAME-WRAP · Feb 2026 — opt-in `wrap`
  // prop skips the `truncate` class so long values (specifically
  // partner business names like "Pristine Water Systems Top End")
  // can flow onto two lines instead of being cut with an ellipsis.
  const valueClasses = [
    "mt-1",
    big ? "text-base" : "text-sm",
    mono ? "font-mono" : "font-semibold",
    "text-slate-900",
    wrap ? "leading-tight break-words" : "truncate",
  ].join(" ");
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">{label}</div>
      <div className={valueClasses}>
        {pill ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 capitalize">
            {String(value || "—").replace(/_/g, " ")}
          </span>
        ) : value}
      </div>
    </div>
  );
}
